import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EmptyState, Spinner } from '../components/Layout';
import { feePolicy as feePolicyApi, errorMessage } from '../utils/api';
import { formatNumber, formatToman } from '../utils/format';
import { EDIT_TYPES } from '../utils/constants';

/*
 * بازمحاسبه‌ی کارانه.
 *
 * کارانه در لحظه‌ی تأیید فنی قفل می‌شود و بعد از آن دست نمی‌خورد — عمدی است،
 * چون ادیتور باید بداند عددی که دید همان است که می‌گیرد. ولی اگر نرخ‌ها آن
 * موقع اشتباه یا هنوز صفر بوده‌اند، باید راهی برای اصلاح باشد.
 *
 * اینجا با مبالغی سروکار داریم که در کارنامه‌ی کارکنان نشسته‌اند، پس هیچ
 * تغییری بدون دیدن پیش‌نمایش و یک تأیید صریح انجام نمی‌شود.
 */

export default function FeeRecalculation() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editType, setEditType] = useState('');
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setConfirming(false);
    try {
      const { data } = await feePolicyApi.previewRecalculation(
        editType ? { edit_type: editType } : {}
      );
      setPreview(data);
    } catch (err) {
      toast.error(errorMessage(err, 'پیش‌نمایش گرفته نشد.'));
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [editType]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await feePolicyApi.recalculate(
        editType ? { edit_type: Number(editType) } : {}
      );
      toast.success(
        `${formatNumber(data.changed)} پرونده اصلاح شد. ${formatNumber(data.editors)} ادیتور مطلع شدند.`
      );
      await load();
    } catch (err) {
      toast.error(errorMessage(err, 'بازمحاسبه انجام نشد.'));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <section className="panel">
      <h2 className="panel__title">بازمحاسبه‌ی کارانه</h2>
      <p className="panel__hint">
        کارانه در لحظه‌ی تأیید مسئول فنی قفل می‌شود و با تغییر نرخ‌ها خودبه‌خود
        عوض نمی‌شود. اگر نرخ‌ها را بعداً وارد یا اصلاح کرده‌اید، اینجا می‌توانید
        پرونده‌های تأییدشده را با نرخ‌های امروز دوباره حساب کنید.
        <br />
        زمان پروژه دست نمی‌خورد — فقط نرخ اعمال می‌شود. هر تغییر با مقدار قبلی و
        بعدی در دفتر رخدادها می‌ماند و ادیتور مربوطه اعلان می‌گیرد.
      </p>

      <div className="toolbar">
        <select
          className="input"
          value={editType}
          onChange={(event) => setEditType(event.target.value)}
        >
          <option value="">همه‌ی انواع پرونده</option>
          {EDIT_TYPES.map((type) => (
            <option key={type.id} value={type.id}>{type.name}</option>
          ))}
        </select>
        <button type="button" className="btn btn--ghost btn--sm" onClick={load} disabled={loading}>
          ↻ بررسی دوباره
        </button>
      </div>

      {loading && <Spinner label="در حال بررسی…" />}

      {!loading && preview && preview.count === 0 && (
        <EmptyState
          icon="✅"
          title="هیچ پرونده‌ای نیاز به تغییر ندارد."
          hint="کارانه‌های ثبت‌شده با نرخ‌های فعلی می‌خوانند."
        />
      )}

      {!loading && preview && preview.count > 0 && (
        <>
          <div className="tiles">
            <div className="tile tile--info">
              <span className="tile__label">پرونده‌های تغییرکننده</span>
              <span className="tile__value num">{formatNumber(preview.count)}</span>
            </div>
            <div className="tile">
              <span className="tile__label">مجموع فعلی</span>
              <span className="tile__value num">{formatToman(preview.total_before)}</span>
            </div>
            <div className="tile tile--brand">
              <span className="tile__label">مجموع پس از بازمحاسبه</span>
              <span className="tile__value num">{formatToman(preview.total_after)}</span>
            </div>
            <div className={`tile ${preview.total_delta >= 0 ? 'tile--success' : 'tile--danger'}`}>
              <span className="tile__label">تفاوت</span>
              <span className="tile__value num">
                {preview.total_delta >= 0 ? '+' : '−'}
                {formatToman(Math.abs(preview.total_delta))}
              </span>
            </div>
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>بیمار</th>
                  <th>نوع</th>
                  <th>ادیتور</th>
                  <th>کارانه فعلی</th>
                  <th>کارانه جدید</th>
                  <th>تفاوت</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td><bdi>{row.edit_type_name}</bdi></td>
                    <td><bdi>{row.editor_name}</bdi></td>
                    <td className="num muted">{formatToman(row.fee_before)}</td>
                    <td className="num">{formatToman(row.fee_after)}</td>
                    <td className={`num ${row.delta >= 0 ? 'delta--up' : 'delta--down'}`}>
                      {row.delta >= 0 ? '+' : '−'}
                      {formatNumber(Math.abs(row.delta))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {preview.truncated && (
            <p className="list-footnote">
              فقط ۲۰۰ ردیف اول نشان داده شده؛ بازمحاسبه روی همه‌ی
              {' '}{formatNumber(preview.count)} پرونده انجام می‌شود.
            </p>
          )}

          {!confirming && (
            <div className="form__actions">
              <button type="button" className="btn btn--primary" onClick={() => setConfirming(true)}>
                بازمحاسبه‌ی {formatNumber(preview.count)} پرونده
              </button>
            </div>
          )}

          {confirming && (
            <div className="alert alert--warning">
              <p>
                <strong>مطمئن هستید؟</strong> کارانه‌ی {formatNumber(preview.count)} پرونده‌ی
                تأییدشده عوض می‌شود و ادیتورها اعلان می‌گیرند. این تغییر در دفتر
                رخدادها ثبت می‌ماند ولی دکمه‌ی بازگشت ندارد — برای برگرداندن باید
                نرخ‌ها را به حالت قبل برگردانید و دوباره بازمحاسبه کنید.
              </p>
              <div className="form__actions" style={{ marginTop: 12, gap: 8 }}>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setConfirming(false)}>
                  انصراف
                </button>
                <button type="button" className="btn btn--primary btn--sm" onClick={apply} disabled={busy}>
                  {busy ? 'در حال اعمال…' : 'بله، اعمال کن'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
